interface IStorage {
  setItem<T>(key: string, value: T): void;
  getItem<T>(key: string): T | null;
  removeItem(key: string): void;
  clear(): void;
  subscribe(key: string, callback: (value: unknown) => void): () => void;
}

export default class StorageService implements IStorage {
  private readonly storage: Storage;
  private listeners: Map<string, Set<(value: unknown) => void>> = new Map();

  constructor(storage?: Storage) {
    if (typeof window !== 'undefined' && window.localStorage) {
      this.storage = storage || window.localStorage;
      window.addEventListener('storage', this.handleStorageChange);
    } else {
      throw new Error('localStorage is not available in this environment');
    }
  }

  private handleStorageChange = (event: StorageEvent) => {
    if (event.key && this.listeners.has(event.key)) {
      const newValue = event.newValue ? JSON.parse(event.newValue) : null;
      this.listeners.get(event.key)?.forEach((callback) => callback(newValue));
    }
  };

  setItem<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      this.storage.setItem(key, serializedValue);
      if (this.listeners.has(key)) {
        this.listeners.get(key)?.forEach((callback) => callback(value));
      }
    } catch (error) {
      console.error(error);
    }
  }

  getItem<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      this.storage.removeItem(key);
      if (this.listeners.has(key)) {
        this.listeners.get(key)?.forEach((callback) => callback(null));
      }
    } catch (error) {
      console.error(error);
    }
  }

  clear(): void {
    try {
      this.storage.clear();
      this.listeners.forEach((callbacks) => {
        callbacks.forEach((callback) => callback(null));
      });
    } catch (error) {
      console.error(error);
    }
  }

  subscribe(key: string, callback: (value: unknown) => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    this.listeners.get(key)!.add(callback);

    return () => {
      const callbacks = this.listeners.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }
}
